import React, { useState, useEffect, useRef } from 'react';
import {
  User, MapPin, CreditCard, Landmark, Users, IndianRupee, Briefcase,
  Save, ArrowLeft, Loader, AlertCircle, CheckCircle,
  Paperclip, Trash2, FileText, Upload, X, QrCode, Download, Printer,
} from 'lucide-react';
import QRCode from 'react-qr-code';
import { employeeAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useDropdownData } from '../utils/useDropdownData';
import { getLocalToday, getDateYearsAgo } from '../utils/dateUtils';
import './MyProfile.css';
import './Employees.css';

// ── Field components (reuse mp-* classes) ────────────────────────────
const Field = ({ label, value, name, editing, onChange, type = 'text', readOnly = false, required = false }) => (
  <div className="mp-field">
    <span className="mp-field-label">{label}{required && <span className="mp-field-req">*</span>}</span>
    {editing && !readOnly ? (
      <div className="mp-field-input-wrap">
        <input className="mp-field-input" type={type} name={name} value={value ?? ''} onChange={onChange} />
      </div>
    ) : (
      <span className="mp-field-value">{value || <span className="mp-field-empty">—</span>}</span>
    )}
  </div>
);

const SelectField = ({ label, value, name, editing, onChange, options, readOnly = false, required = false }) => (
  <div className="mp-field">
    <span className="mp-field-label">{label}{required && <span className="mp-field-req">*</span>}</span>
    {editing && !readOnly ? (
      <div className="mp-field-input-wrap">
        <select className="mp-field-input" name={name} value={value ?? ''} onChange={onChange}
          style={{ cursor: 'pointer' }}>
          <option value="">Select…</option>
          {options.map(o => {
            const v = o.value ?? o;
            const l = o.label ?? o;
            return <option key={v} value={v}>{l}</option>;
          })}
        </select>
      </div>
    ) : (
      <span className="mp-field-value">{value || <span className="mp-field-empty">—</span>}</span>
    )}
  </div>
);

const Section = ({ icon: Icon, title, children, headerRight }) => (
  <div className="mp-section">
    <div className="mp-section-head">
      <Icon size={16} className="mp-section-icon" />
      <h3 className="mp-section-title">{title}</h3>
      {headerRight && <div className="mp-section-head-right">{headerRight}</div>}
    </div>
    <div className="mp-section-body">{children}</div>
  </div>
);

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Human-readable labels for backend Pydantic field names — used to translate
// raw 422 validation errors into friendly "missing field" messages.
const FIELD_LABELS = {
  firstName: 'First Name', lastName: 'Last Name',
  nameAsPerAadhaar: 'Name (Aadhaar)',
  email: 'Email', phone: 'Mobile', mobile: 'Mobile',
  dateOfBirth: 'Date of Birth',
  address: 'Address', city: 'City', state: 'State', zipCode: 'PIN Code',
  department: 'Department', position: 'Designation',
  salary: 'Basic Pay', payFrequency: 'Pay Frequency', startDate: 'Date of Joining',
  bankName: 'Bank Name', accountNumber: 'Account Number', accountType: 'Account Type',
  ifsc: 'IFSC', establishmentId: 'Organization',
};

const humanizeField = (key) => FIELD_LABELS[key] || (key || '')
  .replace(/([A-Z])/g, ' $1')
  .replace(/^./, c => c.toUpperCase())
  .trim();

// Convert a Pydantic-style validation error message into a friendly string.
const parseValidationError = (rawMsg) => {
  if (!rawMsg || typeof rawMsg !== 'string') return rawMsg;
  const trimmed = rawMsg.trim();
  if (!trimmed.startsWith('[') || !trimmed.includes('"loc"')) return rawMsg;
  try {
    const errs = JSON.parse(trimmed);
    if (!Array.isArray(errs) || errs.length === 0) return rawMsg;
    const missing = [];
    const other = [];
    errs.forEach(e => {
      const fieldKey = Array.isArray(e.loc) ? e.loc[e.loc.length - 1] : '';
      const label = humanizeField(fieldKey);
      const isMissingType = e.type === 'missing' || e.msg === 'Field required';
      // string_type / int_type errors with null input also mean "value not provided"
      const isNullOnRequired = (e.type === 'string_type' || e.type === 'int_type' || e.type === 'float_type')
        && (e.input === null || e.input === undefined);
      if (isMissingType || isNullOnRequired) {
        missing.push(label);
      } else {
        other.push(`${label}: ${e.msg}`);
      }
    });
    const parts = [];
    if (missing.length) parts.push(`Missing required field${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`);
    if (other.length) parts.push(other.join('; '));
    return parts.join(' — ') || rawMsg;
  } catch {
    return rawMsg;
  }
};

const IDENTITY_CATEGORIES = [
  { value: 'aadhaar',  label: 'Aadhaar Card' },
  { value: 'pan',      label: 'PAN Card' },
  { value: 'uan',      label: 'UAN Document' },
  { value: 'esic_ip',  label: 'ESIC IP Card' },
  { value: 'other',    label: 'Other Document' },
];

// ── API field mapping ─────────────────────────────────────────────────
const mapApiToForm = (emp) => ({
  nameAsPerAadhaar:           `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
  fatherNameAsPerAadhaar:     emp.father_name_as_per_aadhaar || '',
  motherName:                  emp.mother_name || '',
  civilianExman:               emp.civilian_exman || 'Civilian',
  designation:                 emp.designation || '',
  dateOfBirth:                 emp.date_of_birth || '',
  age:                         emp.age?.toString() || '',
  gender:                      emp.gender || '',
  maritalStatus:               emp.marital_status || '',
  spouseName:                  emp.spouse_name || '',
  bloodGroup:                  emp.blood_group || '',
  educationLevel:              emp.education_level || '',
  // Identity
  pan:                         emp.pan || '',
  aadhar:                      emp.aadhar || emp.aadhaar || '',
  esicIp:                      emp.esic_ip || '',
  uan:                         emp.uan || '',
  pvcReportNo:                 emp.pvc_report_no || '',
  pvcDate:                     emp.pvc_date || '',
  mcDate:                      emp.mc_date || '',
  conductCertificate:          emp.conduct_certificate || '',
  // Barcode
  barcode:                     emp.barcode || '',
  // Contact
  mobile:                      emp.mobile || '',
  email:                       emp.email || '',
  emergencyContactNo:          emp.emergency_contact_no || '',
  nextOfKinName:               emp.next_of_kin_name || '',
  nextOfKinRelation:           emp.next_of_kin_relation || '',
  // Permanent Address
  addressLine1:                emp.address || '',
  addressLine2:                emp.address_line2 || '',
  city:                        emp.city || '',
  state:                       emp.state || '',
  pinCode:                     emp.zip_code || '',
  // Communication Address
  commAddressLine1:            emp.comm_address || '',
  commAddressLine2:            emp.comm_address_line2 || '',
  commCity:                    emp.comm_city || '',
  commState:                   emp.comm_state || '',
  commPinCode:                 emp.comm_zip_code || '',
  // Employment
  department:                  emp.department || '',
  employmentType:              emp.employment_type || '',
  isManagerStaff:              emp.is_manager_staff || false,
  dateOfJoining:               emp.date_of_joining || '',
  contractEndDate:             emp.end_date || '',
  payFrequency:                emp.pay_frequency || 'Monthly',
  // Payroll
  basic:                       emp.basic?.toString() || '',
  da:                          emp.da?.toString() || '',
  hra:                         emp.hra?.toString() || '',
  conveyance:                  emp.conveyance?.toString() || '',
  bonus:                       emp.bonus?.toString() || '',
  otherAllowances:             emp.other_allowances?.toString() || '',
  // Banking
  bankName:                    emp.bank_name || '',
  accountNumber:               emp.account_number || '',
  ifsc:                        emp.ifsc || '',
  // Deductions
  esi:                         emp.esi?.toString() || '',
  epf:                         emp.epf?.toString() || '',
  pt:                          emp.pt?.toString() || '',
  tds:                         emp.tds?.toString() || '',
  society:                     emp.society?.toString() || '',
  insurance:                   emp.insurance?.toString() || '',
  otherDeductions:             emp.other_deductions?.toString() || '',
});

const EMPTY_FORM = {
  nameAsPerAadhaar: '', fatherNameAsPerAadhaar: '', motherName: '',
  civilianExman: 'Civilian', designation: '', dateOfBirth: '', age: '',
  gender: '', maritalStatus: '', spouseName: '', bloodGroup: '', educationLevel: '',
  pan: '', aadhar: '', esicIp: '', uan: '',
  pvcReportNo: '', pvcDate: '', mcDate: '', conductCertificate: '',
  mobile: '', email: '', emergencyContactNo: '', nextOfKinName: '', nextOfKinRelation: '',
  addressLine1: '', addressLine2: '', city: '', state: '', pinCode: '',
  commAddressLine1: '', commAddressLine2: '', commCity: '', commState: '', commPinCode: '',
  department: '', employmentType: '', isManagerStaff: false,
  dateOfJoining: '', contractEndDate: '', payFrequency: 'Monthly',
  basic: '', da: '', hra: '', conveyance: '', bonus: '', otherAllowances: '',
  esi: '', epf: '', pt: '', tds: '', society: '', insurance: '', otherDeductions: '',
};

const EMPLOYMENT_TYPES = ['Full-Time', 'Part-Time', 'Contract', 'Internship', 'Daily-Wage'];
const GENDERS           = ['Male', 'Female', 'Other'];
const MARITAL_STATUSES  = ['Single', 'Married', 'Divorced', 'Widowed'];
const BLOOD_GROUPS      = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
const CIVILIAN_OPTIONS  = ['Civilian', 'Ex-Man'];
const PAY_FREQS         = ['Monthly', 'Weekly', 'Biweekly', 'Daily'];

// ── Main component ────────────────────────────────────────────────────
export default function EmployeeEditForm({ establishmentId, establishmentName, employeeId, onBack }) {
  const isNew = !employeeId;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading]   = useState(!isNew);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState(null);
  const [empCode, setEmpCode]   = useState('');

  // Attachment state
  const [attachOpen, setAttachOpen]         = useState(false);
  const [attachments, setAttachments]       = useState([]);
  const [attachLoading, setAttachLoading]   = useState(false);
  const [uploading, setUploading]           = useState(false);
  const [attachError, setAttachError]       = useState('');
  const [uploadCategory, setUploadCategory] = useState('aadhaar');
  const fileInputRef = useRef(null);

  const today    = getLocalToday();
  const maxDOB   = getDateYearsAgo(18);

  const { departments, fetchDepartmentsByEstablishment } = useDropdownData();

  useEffect(() => {
    if (establishmentId) fetchDepartmentsByEstablishment(establishmentId);
  }, [establishmentId, fetchDepartmentsByEstablishment]);

  useEffect(() => {
    if (!isNew) {
      setLoading(true);
      employeeAPI.getById(employeeId)
        .then(data => {
          setFormData(mapApiToForm(data));
          setEmpCode(data.employee_id || '');
        })
        .catch(err => setToast({ type: 'error', msg: err.message || 'Could not load employee' }))
        .finally(() => setLoading(false));
    }
  }, [employeeId, isNew]);

  // Auto-calculate age when DOB changes
  useEffect(() => {
    if (formData.dateOfBirth) {
      // Parse YYYY-MM-DD as local date — new Date(dateOnlyString) treats it
      // as UTC midnight which can shift the day in TZs west of UTC.
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(formData.dateOfBirth);
      const dob   = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(formData.dateOfBirth);
      const today = new Date();
      let age     = today.getFullYear() - dob.getFullYear();
      const md    = today.getMonth() - dob.getMonth();
      if (md < 0 || (md === 0 && today.getDate() < dob.getDate())) age--;
      if (age >= 0) setFormData(prev => ({ ...prev, age: age.toString() }));
    }
  }, [formData.dateOfBirth]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = e => {
    const { name, value, type: t, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: t === 'checkbox' ? checked : value }));
  };

  // --- Attachment handlers ---
  const loadAttachments = async (empId) => {
    setAttachLoading(true);
    setAttachError('');
    try {
      const data = await employeeAPI.getAttachments(empId);
      setAttachments(data.attachments || data || []);
    } catch {
      setAttachError('Could not load attachments.');
    } finally {
      setAttachLoading(false);
    }
  };

  const handleToggleAttach = () => {
    if (!attachOpen) loadAttachments(employeeId);
    setAttachOpen(prev => !prev);
    setAttachError('');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setAttachError('');
    try {
      await employeeAPI.uploadAttachment(file, employeeId, uploadCategory);
      await loadAttachments(employeeId);
    } catch (err) {
      setAttachError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    setAttachError('');
    try {
      await employeeAPI.deleteAttachment(employeeId, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch {
      setAttachError('Could not delete attachment.');
    }
  };

  const handleSave = async () => {
    // Minimum required fields to create/update an employee record.
    // Everything else can be filled in later via Edit.
    if (!formData.nameAsPerAadhaar?.trim()) {
      setToast({ type: 'error', msg: 'Employee name is required.' });
      return;
    }
    if (isNew && !formData.mobile?.trim()) {
      setToast({ type: 'error', msg: 'Mobile number is required.' });
      return;
    }
    if (isNew && !formData.department) {
      setToast({ type: 'error', msg: 'Department is required.' });
      return;
    }

    setSaving(true);
    setToast(null);

    const nameParts = (formData.nameAsPerAadhaar || '').trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName  = nameParts.slice(1).join(' ') || '';

    try {
      // Helper: empty string → null so Pydantic Optional fields don't reject ''
      const e = (v) => (v && String(v).trim() ? String(v).trim() : null);
      if (isNew) {
        await employeeAPI.create({
          establishmentId,
          personal: {
            firstName, lastName: lastName || null,
            email: e(formData.email),
            phone: formData.mobile,
            dateOfBirth: e(formData.dateOfBirth),
            address: e(formData.addressLine1),
            city: e(formData.city),
            state: e(formData.state),
            zipCode: e(formData.pinCode),
            civilianExman: formData.civilianExman,
            designation: formData.designation,
            nameAsPerAadhaar: formData.nameAsPerAadhaar,
            fatherNameAsPerAadhaar: formData.fatherNameAsPerAadhaar,
            motherName: formData.motherName,
            maritalStatus: formData.maritalStatus,
            gender: formData.gender,
            spouseName: formData.spouseName,
            age: parseInt(formData.age) || null,
            educationLevel: formData.educationLevel,
            pan: formData.pan,
            aadhar: formData.aadhar,
            esicIp: formData.esicIp,
            uan: formData.uan,
            mobile: formData.mobile,
            emergencyContactNo: formData.emergencyContactNo,
            nextOfKinName: formData.nextOfKinName,
            nextOfKinRelation: formData.nextOfKinRelation,
            bloodGroup: formData.bloodGroup,
          },
          payroll: {
            department: formData.department || 'General',
            position: formData.designation || 'Employee',
            salary: parseFloat(formData.basic) || 0,
            payFrequency: formData.payFrequency || 'Monthly',
            startDate: formData.dateOfJoining || today,
            employmentType: formData.employmentType,
            isManagerStaff: formData.isManagerStaff || false,
            basic: parseFloat(formData.basic) || null,
            da: parseFloat(formData.da) || null,
            hra: parseFloat(formData.hra) || null,
            conveyance: parseFloat(formData.conveyance) || null,
            bonus: parseFloat(formData.bonus) || null,
            otherAllowances: parseFloat(formData.otherAllowances) || null,
          },
          banking: {
            bankName: e(formData.bankName),
            accountType: 'Savings',
            accountNumber: e(formData.accountNumber),
            ifsc: e(formData.ifsc),
          },
          deductions: {
            esi: parseFloat(formData.esi) || null,
            epf: parseFloat(formData.epf) || null,
            pt: parseFloat(formData.pt) || null,
            tds: parseFloat(formData.tds) || null,
            society: parseFloat(formData.society) || null,
            insurance: parseFloat(formData.insurance) || null,
            otherDeductions: parseFloat(formData.otherDeductions) || 0,
          },
        });
      } else {
        // Update all sections
        await employeeAPI.update(employeeId, {
          section: 'personal',
          data: {
            firstName, lastName,
            nameAsPerAadhaar: formData.nameAsPerAadhaar,
            fatherNameAsPerAadhaar: formData.fatherNameAsPerAadhaar,
            motherName: formData.motherName,
            email: formData.email,
            phone: formData.mobile,
            mobile: formData.mobile,
            dateOfBirth: formData.dateOfBirth,
            age: formData.age ? parseInt(formData.age) : null,
            gender: formData.gender,
            maritalStatus: formData.maritalStatus,
            spouseName: formData.spouseName,
            designation: formData.designation,
            civilianExman: formData.civilianExman,
            educationLevel: formData.educationLevel,
            uan: formData.uan,
            aadhar: formData.aadhar,
            pan: formData.pan,
            esicIp: formData.esicIp,
            bloodGroup: formData.bloodGroup,
            address: formData.addressLine1,
            addressLine2: formData.addressLine2,
            city: formData.city,
            state: formData.state,
            zipCode: formData.pinCode,
            commAddress: formData.commAddressLine1,
            commAddressLine2: formData.commAddressLine2,
            commCity: formData.commCity,
            commState: formData.commState,
            commZipCode: formData.commPinCode,
            emergencyContactNo: formData.emergencyContactNo,
            nextOfKinName: formData.nextOfKinName,
            nextOfKinRelation: formData.nextOfKinRelation,
            pvcReportNo: formData.pvcReportNo,
            pvcDate: formData.pvcDate,
            mcDate: formData.mcDate,
            conductCertificate: formData.conductCertificate,
          },
        });
        await employeeAPI.update(employeeId, {
          section: 'banking',
          data: {
            accountNumber: formData.accountNumber,
            ifsc: formData.ifsc,
            bankName: formData.bankName,
            accountType: 'Savings',
          },
        });
        await employeeAPI.update(employeeId, {
          section: 'payroll',
          data: {
            department: formData.department,
            position: formData.designation,
            employmentType: formData.employmentType,
            isManagerStaff: formData.isManagerStaff || false,
            salary: parseFloat(formData.basic) || null,
            basic: parseFloat(formData.basic) || null,
            da: parseFloat(formData.da) || null,
            hra: parseFloat(formData.hra) || null,
            conveyance: parseFloat(formData.conveyance) || null,
            bonus: parseFloat(formData.bonus) || null,
            otherAllowances: parseFloat(formData.otherAllowances) || null,
            payFrequency: formData.payFrequency || 'Monthly',
            startDate: formData.dateOfJoining,
            endDate: formData.contractEndDate,
          },
        });
        await employeeAPI.update(employeeId, {
          section: 'deductions',
          data: {
            esic: parseFloat(formData.esi) || null,
            pf: parseFloat(formData.epf) || null,
            pt: parseFloat(formData.pt) || null,
            tds: parseFloat(formData.tds) || null,
            society: parseFloat(formData.society) || null,
            insurance: parseFloat(formData.insurance) || null,
            otherDeductions: parseFloat(formData.otherDeductions) || null,
          },
        });
      }

      notifySavedAndReload(isNew ? 'Employee created successfully.' : 'Employee updated successfully.');
    } catch (err) {
      const friendlyMsg = parseValidationError(err.message) || 'Failed to save employee.';
      setToast({ type: 'error', msg: friendlyMsg });
    } finally {
      setSaving(false);
    }
  };

  const initials = formData.nameAsPerAadhaar
    ? formData.nameAsPerAadhaar.trim().split(' ').slice(0, 2).map(w => w[0] ?? '').join('').toUpperCase()
    : isNew ? '+' : 'E';

  const deptOptions = departments.length > 0
    ? departments.map(d => ({ value: d.name ?? d, label: d.name ?? d }))
    : [];

  if (loading) return (
    <div className="mp-state-center" style={{ marginTop: '3rem' }}>
      <Loader size={28} className="mp-spinner" />
      <p>Loading employee…</p>
    </div>
  );

  return (
    <div className="mp-page">
      {/* Hero */}
      <div className="mp-hero">
        <div className="mp-avatar">{initials}</div>
        <div className="mp-hero-info">
          <h1 className="mp-hero-name">
            {isNew ? 'New Employee' : (formData.nameAsPerAadhaar || 'Employee')}
          </h1>
          <div className="mp-hero-meta">
            {formData.designation && <span>{formData.designation}</span>}
            {formData.department && <><span className="mp-dot">·</span><span>{formData.department}</span></>}
            {establishmentName && <><span className="mp-dot">·</span><span>{establishmentName}</span></>}
          </div>
          {!isNew && empCode && <div className="mp-hero-id">Employee ID: {empCode}</div>}
        </div>
        {formData.barcode && (
          <div style={{ position: 'relative' }} className="ue-barcode-wrap">
            <button
              type="button"
              title="View barcode"
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
              onClick={(e) => {
                const popup = e.currentTarget.nextElementSibling;
                popup.style.display = popup.style.display === 'none' ? 'block' : 'none';
              }}
            >
              <QrCode size={14} /> Barcode
            </button>
            <div style={{ display: 'none', position: 'absolute', top: '100%', right: 0, zIndex: 50, marginTop: '0.5rem', background: 'var(--theme-surface)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', textAlign: 'center', minWidth: '220px' }}>
              <button
                type="button"
                onClick={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px', lineHeight: 1 }}
                title="Close"
              >
                <X size={16} />
              </button>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--theme-text)', marginBottom: '0.75rem' }}>
                {formData.nameAsPerAadhaar || 'Employee'}
              </div>
              <div id="qr-card-content">
                <QRCode value={formData.barcode} size={160} />
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#4a5568', fontFamily: 'monospace', letterSpacing: '0.03em' }}>{formData.barcode}</div>
              {empCode && <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.15rem' }}>ID: {empCode}</div>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', justifyContent: 'center' }}>
                <button
                  type="button"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => {
                    const svg = document.querySelector('#qr-card-content svg');
                    if (!svg) return;
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const img = new Image();
                    const name = formData.nameAsPerAadhaar || 'Employee';
                    img.onload = () => {
                      canvas.width = 300; canvas.height = 380;
                      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 300, 380);
                      ctx.fillStyle = '#1e293b'; ctx.font = 'bold 16px sans-serif'; ctx.textAlign = 'center';
                      ctx.fillText(name, 150, 30);
                      ctx.drawImage(img, 50, 50, 200, 200);
                      ctx.fillStyle = '#4a5568'; ctx.font = '11px monospace';
                      ctx.fillText(formData.barcode, 150, 280);
                      if (empCode) { ctx.fillStyle = '#64748b'; ctx.font = '11px sans-serif'; ctx.fillText('ID: ' + empCode, 150, 300); }
                      const link = document.createElement('a');
                      link.download = `barcode-${empCode || 'employee'}.png`;
                      link.href = canvas.toDataURL('image/png');
                      link.click();
                    };
                    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                  }}
                >
                  <Download size={12} /> Download
                </button>
                <button
                  type="button"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => {
                    const svg = document.querySelector('#qr-card-content svg');
                    if (!svg) return;
                    const name = formData.nameAsPerAadhaar || 'Employee';
                    const svgHtml = new XMLSerializer().serializeToString(svg);
                    const win = window.open('', '_blank', 'width=400,height=500');
                    win.document.write(`<html><head><title>Barcode - ${name}</title></head><body style="text-align:center;font-family:sans-serif;padding:2rem;">
                      <h2 style="margin:0 0 1rem">${name}</h2>
                      ${svgHtml}
                      <p style="font-family:monospace;font-size:12px;margin-top:0.75rem">${formData.barcode}</p>
                      ${empCode ? `<p style="font-size:12px;color:#666">ID: ${empCode}</p>` : ''}
                      <script>setTimeout(()=>{window.print();},400);<\/script>
                    </body></html>`);
                    win.document.close();
                  }}
                >
                  <Printer size={12} /> Print
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="mp-hero-actions">
          <button className="mp-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? <Loader size={15} className="mp-spinner-sm" /> : <Save size={15} />}
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="mp-btn-cancel" onClick={onBack} disabled={saving}>
            <ArrowLeft size={15} /> Back
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`mp-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Sections grid */}
      <div className="mp-grid">

        {/* Employment Info */}
        <Section icon={Briefcase} title="Employment Info">
          <Field label="Organization" value={establishmentName || establishmentId} name="est" editing={false} />
          {deptOptions.length > 0
            ? <SelectField label="Department"      value={formData.department}      name="department"      editing={true} onChange={handleChange} options={deptOptions} required={isNew} />
            : <Field       label="Department"      value={formData.department}      name="department"      editing={true} onChange={handleChange} required={isNew} />
          }
          <Field label="Designation"    value={formData.designation}    name="designation"    editing={true} onChange={handleChange} />
          <SelectField label="Emp. Type"  value={formData.employmentType} name="employmentType" editing={true} onChange={handleChange} options={EMPLOYMENT_TYPES} />
          <Field label="Date of Joining" value={formData.dateOfJoining}  name="dateOfJoining"  editing={true} onChange={handleChange} type="date" />
          <Field label="End Date"        value={formData.contractEndDate} name="contractEndDate" editing={true} onChange={handleChange} type="date" />
          <SelectField label="Pay Frequency" value={formData.payFrequency} name="payFrequency" editing={true} onChange={handleChange} options={PAY_FREQS} />
        </Section>

        {/* Personal Details */}
        <Section icon={User} title="Personal Details">
          <Field label="Name (Aadhaar)"  value={formData.nameAsPerAadhaar}       name="nameAsPerAadhaar"       editing={true} onChange={handleChange} required />
          <Field label="Father's Name"   value={formData.fatherNameAsPerAadhaar} name="fatherNameAsPerAadhaar" editing={true} onChange={handleChange} />
          <Field label="Mother's Name"   value={formData.motherName}             name="motherName"             editing={true} onChange={handleChange} />
          <SelectField label="Type"      value={formData.civilianExman}          name="civilianExman"          editing={true} onChange={handleChange} options={CIVILIAN_OPTIONS} />
          <Field label="Date of Birth"   value={formData.dateOfBirth}            name="dateOfBirth"            editing={true} onChange={handleChange} type="date" />
          <Field label="Age"             value={formData.age}                    name="age"                    editing={false} />
          <SelectField label="Gender"    value={formData.gender}                 name="gender"                 editing={true} onChange={handleChange} options={GENDERS} />
          <SelectField label="Marital Status" value={formData.maritalStatus}    name="maritalStatus"          editing={true} onChange={handleChange} options={MARITAL_STATUSES} />
          <Field label="Spouse Name"     value={formData.spouseName}             name="spouseName"             editing={true} onChange={handleChange} />
          <SelectField label="Blood Group" value={formData.bloodGroup}           name="bloodGroup"             editing={true} onChange={handleChange} options={BLOOD_GROUPS} />
          <Field label="Education"       value={formData.educationLevel}         name="educationLevel"         editing={true} onChange={handleChange} />
        </Section>

        {/* Identity & Documents */}
        <Section
          icon={CreditCard}
          title="Identity & Documents"
          headerRight={!isNew && (
            <button
              className={`mp-btn-attach ${attachOpen ? 'active' : ''}`}
              onClick={handleToggleAttach}
              title="Upload / view identity document attachments"
            >
              <Paperclip size={14} />
              {attachments.length > 0 && <span className="mp-attach-badge">{attachments.length}</span>}
            </button>
          )}
        >
          <Field label="PAN"      value={formData.pan}             name="pan"             editing={true} onChange={handleChange} />
          <Field label="Aadhaar"  value={formData.aadhar}          name="aadhar"          editing={true} onChange={handleChange} />
          <Field label="ESIC IP"  value={formData.esicIp}          name="esicIp"          editing={true} onChange={handleChange} />
          <Field label="UAN"      value={formData.uan}             name="uan"             editing={true} onChange={handleChange} />
          <Field label="PVC No."  value={formData.pvcReportNo}     name="pvcReportNo"     editing={true} onChange={handleChange} />
          <Field label="PVC Date" value={formData.pvcDate}         name="pvcDate"         editing={true} onChange={handleChange} type="date" />
          <Field label="MC Date"  value={formData.mcDate}          name="mcDate"          editing={true} onChange={handleChange} type="date" />
          <Field label="Conduct Cert." value={formData.conductCertificate} name="conductCertificate" editing={true} onChange={handleChange} />
        </Section>

        {/* Contact */}
        <Section icon={Users} title="Contact">
          <Field label="Mobile"            value={formData.mobile}             name="mobile"             editing={true} onChange={handleChange} required={isNew} />
          <Field label="Email"             value={formData.email}              name="email"              editing={true} onChange={handleChange} type="email" />
          <Field label="Emergency Contact" value={formData.emergencyContactNo} name="emergencyContactNo" editing={true} onChange={handleChange} />
          <Field label="Next of Kin"       value={formData.nextOfKinName}      name="nextOfKinName"      editing={true} onChange={handleChange} />
          <Field label="Kin Relation"      value={formData.nextOfKinRelation}  name="nextOfKinRelation"  editing={true} onChange={handleChange} />
        </Section>

        {/* Permanent Address */}
        <Section icon={MapPin} title="Permanent Address">
          <Field label="Address Line 1" value={formData.addressLine1} name="addressLine1" editing={true} onChange={handleChange} />
          <Field label="Address Line 2" value={formData.addressLine2} name="addressLine2" editing={true} onChange={handleChange} />
          <Field label="City"           value={formData.city}         name="city"         editing={true} onChange={handleChange} />
          <Field label="State"          value={formData.state}        name="state"        editing={true} onChange={handleChange} />
          <Field label="PIN Code"       value={formData.pinCode}      name="pinCode"      editing={true} onChange={handleChange} />
        </Section>

        {/* Communication Address */}
        <Section icon={MapPin} title="Communication Address">
          <Field label="Address Line 1" value={formData.commAddressLine1} name="commAddressLine1" editing={true} onChange={handleChange} />
          <Field label="Address Line 2" value={formData.commAddressLine2} name="commAddressLine2" editing={true} onChange={handleChange} />
          <Field label="City"           value={formData.commCity}         name="commCity"         editing={true} onChange={handleChange} />
          <Field label="State"          value={formData.commState}        name="commState"        editing={true} onChange={handleChange} />
          <Field label="PIN Code"       value={formData.commPinCode}      name="commPinCode"      editing={true} onChange={handleChange} />
        </Section>

        {/* Bank Details */}
        <Section icon={Landmark} title="Bank Details">
          <Field label="Bank Name"    value={formData.bankName}      name="bankName"      editing={true} onChange={handleChange} />
          <Field label="Account No."  value={formData.accountNumber} name="accountNumber" editing={true} onChange={handleChange} />
          <Field label="IFSC"         value={formData.ifsc}          name="ifsc"          editing={true} onChange={handleChange} />
        </Section>

        {/* Payroll & Deductions */}
        <Section icon={IndianRupee} title="Payroll & Deductions">
          <Field label="Basic (₹)"          value={formData.basic}          name="basic"          editing={true} onChange={handleChange} type="number" />
          <Field label="DA (₹)"             value={formData.da}             name="da"             editing={true} onChange={handleChange} type="number" />
          <Field label="HRA (₹)"            value={formData.hra}            name="hra"            editing={true} onChange={handleChange} type="number" />
          <Field label="Conveyance (₹)"     value={formData.conveyance}     name="conveyance"     editing={true} onChange={handleChange} type="number" />
          <Field label="Bonus (₹)"          value={formData.bonus}          name="bonus"          editing={true} onChange={handleChange} type="number" />
          <Field label="Other Allow. (₹)"   value={formData.otherAllowances} name="otherAllowances" editing={true} onChange={handleChange} type="number" />
          <div className="est-section-divider" />
          <Field label="ESI (₹)"            value={formData.esi}            name="esi"            editing={true} onChange={handleChange} type="number" />
          <Field label="EPF (₹)"            value={formData.epf}            name="epf"            editing={true} onChange={handleChange} type="number" />
          <Field label="PT (₹)"             value={formData.pt}             name="pt"             editing={true} onChange={handleChange} type="number" />
          <Field label="TDS (₹)"            value={formData.tds}            name="tds"            editing={true} onChange={handleChange} type="number" />
          <Field label="Society (₹)"        value={formData.society}        name="society"        editing={true} onChange={handleChange} type="number" />
          <Field label="Insurance (₹)"      value={formData.insurance}      name="insurance"      editing={true} onChange={handleChange} type="number" />
          <Field label="Other Deduct. (₹)"  value={formData.otherDeductions} name="otherDeductions" editing={true} onChange={handleChange} type="number" />
        </Section>

        {/* Form-level submit row — mirrors the header Save button so users don't
            have to scroll back to the top after filling out the form. */}
        <div className="mp-form-actions">
          <button className="mp-btn-cancel" onClick={onBack} disabled={saving}>
            <ArrowLeft size={15} /> Cancel
          </button>
          <button className="mp-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? <Loader size={15} className="mp-spinner-sm" /> : <Save size={15} />}
            {saving ? 'Saving…' : (isNew ? 'Create Employee' : 'Save Changes')}
          </button>
        </div>

      </div>

      {/* Attachment Modal */}
      {attachOpen && (
        <div className="adoc-backdrop" onClick={() => setAttachOpen(false)}>
          <div className="adoc-modal" onClick={e => e.stopPropagation()}>

            <div className="adoc-header">
              <span className="adoc-title">Upload Identity Documents</span>
              <button className="adoc-close" onClick={() => setAttachOpen(false)}><X size={16} /></button>
            </div>

            <div className="adoc-body">
              <div className="adoc-row">
                <label className="adoc-label">Document Type</label>
                <div className="adoc-controls">
                  <select
                    className="adoc-select"
                    value={uploadCategory}
                    onChange={e => setUploadCategory(e.target.value)}
                    disabled={uploading}
                  >
                    {IDENTITY_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <button
                    className="adoc-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading
                      ? <><Loader size={14} className="mp-spinner-sm" /> Uploading…</>
                      : <><Upload size={14} /> Upload File</>
                    }
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                </div>
                <p className="adoc-hint">Accepted formats: PDF, JPG, PNG, DOC, DOCX</p>
              </div>

              {attachError && (
                <p className="adoc-error"><AlertCircle size={13} /> {attachError}</p>
              )}

              <div className="adoc-list-wrap">
                {attachLoading ? (
                  <div className="adoc-state"><Loader size={20} className="mp-spinner" /><span>Loading…</span></div>
                ) : attachments.length === 0 ? (
                  <div className="adoc-state adoc-empty">
                    <FileText size={32} />
                    <span>No documents uploaded yet</span>
                  </div>
                ) : (
                  attachments.map(att => (
                    <div key={att.id} className="adoc-file">
                      <FileText size={16} className="adoc-file-ico" />
                      <div className="adoc-file-info">
                        <span className="adoc-file-name">{att.original_file_name || att.file_name}</span>
                        <span className="adoc-file-meta">
                          {IDENTITY_CATEGORIES.find(c => c.value === att.category)?.label || att.category}
                          {att.file_size ? ` · ${fmtSize(att.file_size)}` : ''}
                        </span>
                      </div>
                      {att.s3_url && (
                        <a href={att.s3_url} target="_blank" rel="noopener noreferrer" className="adoc-dl">Download</a>
                      )}
                      <button className="adoc-del" onClick={() => handleDeleteAttachment(att.id)} title="Delete">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
